import type React from 'react';
import { useMemo } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { Button, cn } from '@crehana/compass.ui/v2';
import { useTranslation } from '@crehana/i18n';
import {
  Col,
  Container,
  InverseButton,
  type InverseButtonProps,
  type PrimaryButtonProps,
  ResponsiveText,
  Row,
  Tab,
  Tabs,
  Tooltip,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';
import { CapitalizeFirstLetter } from '@crehana/utils';
import { useUITheme } from '@crehana/web';
import { useRouter } from 'next/router';

import type { BaseRouteType } from '@/shared/components/DashboardTemplate/types';
import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

interface HeaderTabsProps {
  nestedRoutes: BaseRouteType[];
  position?: 'static' | 'sticky';
  transparent?: boolean;
  cta?: PrimaryButtonProps & { loading?: boolean };
  cta2?: InverseButtonProps;
  actions?: React.ReactNode;
  tooltipLabel?: string;
}

const HeaderTabs: React.FC<React.PropsWithChildren<HeaderTabsProps>> = ({
  nestedRoutes,
  position = 'static',
  transparent,
  cta,
  cta2,
  actions,
  tooltipLabel,
}) => {
  const { slug } = useAdminDashboard();
  const router = useRouter();
  const { isDark } = useUITheme();
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();

  const currentIndex = useMemo(() => {
    const rootPath = router.pathname;

    return nestedRoutes.findIndex(r =>
      [rootPath, `${rootPath}/`].includes(r.path),
    );
  }, [router.pathname, nestedRoutes]);

  const changePage = (value: number) => {
    const sections = router.pathname
      .replace('/org/[slug]/panel/', '')
      .split('/');
    const url = router.pathname.replace('[slug]', slug);
    const tab = t(nestedRoutes[value].label);

    logEvent({
      event: 'Tab Navigation',
      data: {
        ...getSourceEventParams(),
        Organization: slug,
        'Section Menu': CapitalizeFirstLetter(sections[0]) || '',
        Tab: tab || '',
        Url: url,
      },
    });

    router.push({
      pathname: nestedRoutes[value].path,
      query: { slug },
    });
  };

  return (
    <div
      className={cn(
        'tw-w-full',
        position,
        // TODO: Use :dark directive when we have a dark theme available in B2B Admin
        transparent || isDark ? 'bg-base-dark' : 'tw-bg-neutral-light-100',
        'tw-top-0 tw-right-0 tw-left-0 tw-z-5',
      )}
    >
      <Container type="dashboard">
        <Row>
          <Col xs={12} sm={12} md={9} lg={10}>
            {nestedRoutes.length > 0 && (
              <Tabs
                value={currentIndex}
                dark={isDark}
                onTabChange={value => changePage(value)}
              >
                {nestedRoutes.map(route => (
                  <Tab
                    data-tour={route.tourSelector}
                    key={route.key}
                    className="tw-font-link2"
                    disabled={route.disable}
                    size="s"
                  >
                    <>
                      {!route.availableSoon && (
                        <ResponsiveText text={t(route.label)} />
                      )}
                      {route.availableSoon && (
                        <Tooltip
                          label={t('TABS_SOON_AVAILABLE_TITLE')}
                          position="right"
                          type="regular"
                          isDark={!isDark}
                          key={`headerTab-${t(route.label)}}`}
                        >
                          <p className="button-base relative h-56 px-16 font-subtitle2 text-gray-dark-56 cursor-default">
                            {t(route.label)}
                          </p>
                        </Tooltip>
                      )}
                    </>
                  </Tab>
                ))}
              </Tabs>
            )}
          </Col>
          <Col
            xs={12}
            sm={4}
            md={3}
            lg={2}
            offset={{ xs: 0, sm: 8, md: 0, lg: 0 }}
            className="flex items-center justify-end"
          >
            {actions}

            {cta2 && (
              <InverseButton
                {...cta2}
                darkMode={isDark}
                size={'s'}
                style={{ marginRight: 16 }}
              />
            )}

            {cta && !tooltipLabel && (
              <Button
                size="xs"
                type="button"
                variant="primary"
                onClick={cta.onClick}
                data-tour="header-tabs-cta"
                disabled={cta.loading}
              >
                <ResponsiveText text={cta.label || ''} />
              </Button>
            )}

            {cta && tooltipLabel && (
              <Tooltip
                label={t(tooltipLabel || '')}
                type="regular"
                isDark={!isDark}
                position="left"
                className="flex items-center whitespace-pre-line"
              >
                <Button
                  size="xs"
                  type="button"
                  variant="primary"
                  data-tour="header-tabs-cta"
                  onClick={cta.onClick}
                  disabled={cta.loading}
                >
                  <ResponsiveText text={cta.label || ''} />
                </Button>
              </Tooltip>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HeaderTabs;
